import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { CheckCircle, Eye, Save, Archive } from 'lucide-react'
import { formatHumanDate } from '../../lib/dateUtils'
import Layout from '../../components/Layout'

export default function TournamentPublished() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [tournament, setTournament] = useState(null)

  useEffect(() => {
    // Get tournament data from localStorage
    const storedTournament = localStorage.getItem(`tournament_${id}`)
    if (storedTournament) {
      setTournament(JSON.parse(storedTournament))
    }
  }, [id])

  const handleUnpublish = () => {
    if (tournament) {
      tournament.status = 'draft'
      delete tournament.publishedAt
      localStorage.setItem(`tournament_${id}`, JSON.stringify(tournament))
      navigate(`/admin/tournaments/${id}/populate-brackets`)
    }
  }

  const handleSaveAsDraft = () => {
    if (tournament) {
      tournament.status = 'draft'
      delete tournament.publishedAt
      localStorage.setItem(`tournament_${id}`, JSON.stringify(tournament))
      navigate('/admin/tournaments')
    }
  }

  const handleViewBrackets = () => {
    navigate(`/predictions/create?bracketId=${id}-0`)
  }

  if (!tournament) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading tournament...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-4">
            <CheckCircle className="h-10 w-10 text-success" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Tournament Published!</h1>
          <p className="text-muted-foreground">
            Your tournament is now live and visible to all users
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{tournament.title}</CardTitle>
            <CardDescription>
              Published on {formatHumanDate(tournament.publishedAt)} at {new Date(tournament.publishedAt).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Tournament Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Divisions:</span>
                    <p className="font-medium">{tournament.divisions?.length || 0} divisions</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Start Date:</span>
                    <p className="font-medium">{tournament.startDate || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">End Date:</span>
                    <p className="font-medium">{tournament.endDate || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {tournament.divisions && tournament.divisions.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Divisions</h3>
                  <div className="flex flex-wrap gap-2">
                    {tournament.divisions.map((div, idx) => (
                      <div key={idx} className="px-3 py-1 bg-muted rounded-md text-sm">
                        {div.name || div}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <Button 
            variant="primaryNeon"
            onClick={handleViewBrackets} 
            className="w-full"
            size="lg"
          >
            <Eye className="h-5 w-5 mr-2" />
            View Tournament Brackets
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <Button 
              onClick={handleUnpublish}
              variant="outline"
              size="lg"
            >
              <Archive className="h-5 w-5 mr-2" />
              Unpublish
            </Button>
            <Button 
              onClick={handleSaveAsDraft}
              variant="outline"
              size="lg"
            >
              <Save className="h-5 w-5 mr-2" />
              Save as Draft
            </Button>
          </div>

          <Button 
            onClick={() => navigate('/admin/tournaments')}
            variant="ghost"
            className="w-full"
          >
            Back to Tournament Management
          </Button>
        </div>
      </div>
    </Layout>
  )
}
